import { z } from 'zod';

import { PlaceIdParamsSchema } from '../customer/place-customer.schemas.js';
import { RegisterPaymentLineBodySchema } from '../finance/finance.schemas.js';

import { FISCAL_DOCUMENT_KINDS } from './rule-engine.schemas.js';

export const FISCAL_DOCUMENT_STATUSES = [
  'DRAFT',
  'CALCULATED',
  'PENDING_AUTHORIZATION',
  'AUTHORIZED',
  'REJECTED',
  'CANCELLED',
  'DENIED',
] as const;

export const FiscalDocumentIdParamsSchema = z.looseObject({
  id: z.uuid(),
});

export const GetFiscalDocumentParamsSchema = z.looseObject({
  placeId: z.uuid(),
  id: z.uuid(),
});

export const ListFiscalDocumentsParamsSchema = PlaceIdParamsSchema;

export const ListFiscalDocumentsQuerySchema = z.looseObject({
  status: z.enum(FISCAL_DOCUMENT_STATUSES).optional(),
  documentKind: z.enum(FISCAL_DOCUMENT_KINDS).optional(),
  competenceFrom: z.iso.date().optional(),
  competenceTo: z.iso.date().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export const FiscalDocumentListItemSchema = z.looseObject({
  id: z.uuid(),
  documentKind: z.enum(FISCAL_DOCUMENT_KINDS),
  status: z.enum(FISCAL_DOCUMENT_STATUSES),
  /** OUTBOUND = Saída (customer); INBOUND = Entrada (supplier). */
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  competenceDate: z.iso.date(),
  customerRoleId: z.uuid().nullable(),
  counterpartyName: z.string().nullable(),
  counterpartyDocument: z.string().nullable(),
  primaryCfopCode: z.string().nullable(),
  grossTotalCents: z.string(),
  discountTotalCents: z.string(),
  netTotalCents: z.string(),
  currency: z.string(),
  createdAt: z.iso.datetime(),
  series: z.string().nullable(),
  number: z.string().regex(/^\d+$/).nullable(),
});

/** Party snapshot for recipient/issuer display on document detail (live join, not a fiscal snapshot). */
export const FiscalDocumentCounterpartySchema = z.looseObject({
  customerRoleId: z.uuid(),
  name: z.string().nullable(),
  documentNumber: z.string().nullable(),
  tradeName: z.string().nullable(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  district: z.string().nullable(),
  cityName: z.string().nullable(),
  stateCode: z.string().nullable(),
  zipCode: z.string().nullable(),
  complement: z.string().nullable(),
  stateRegistration: z.string().nullable(),
});

export const ListFiscalDocumentsResponseSchema = z.looseObject({
  items: z.array(FiscalDocumentListItemSchema),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
  }),
});

export const FiscalDocumentItemDetailSchema = z.looseObject({
  id: z.uuid(),
  lineNumber: z.number().int(),
  itemType: z.enum(['PRODUCT', 'SERVICE']),
  description: z.string(),
  quantityBp: z.string(),
  unitPriceCents: z.string(),
  grossTotalCents: z.string(),
  discountCents: z.string(),
  netTotalCents: z.string(),
  currency: z.string(),
  ncmCode: z.string().nullable(),
  ncmOrigin: z.enum(['TENANT_PROFILE', 'GENERIC_INHERITANCE', 'MANUAL']).nullable(),
  unidentifiedPart: z.boolean(),
  cfopCode: z.string().nullable(),
  calculation: z
    .looseObject({
      icmsValueCents: z.string().nullable(),
      pisValueCents: z.string().nullable(),
      cofinsValueCents: z.string().nullable(),
      ipiValueCents: z.string().nullable(),
      issValueCents: z.string().nullable(),
    })
    .nullable(),
});

export const FiscalDocumentLatestSnapshotSchema = z.looseObject({
  snapshotId: z.uuid(),
  placeTaxProfileId: z.uuid(),
  taxRuleSetId: z.uuid(),
  calculatedAt: z.iso.datetime(),
});

export const GetFiscalDocumentResponseSchema = z.looseObject({
  id: z.uuid(),
  placeId: z.uuid(),
  documentKind: z.enum(FISCAL_DOCUMENT_KINDS),
  status: z.enum(FISCAL_DOCUMENT_STATUSES),
  inboundOperationalStatus: z.enum(['AWAITING_CONFERENCE', 'FINALIZED']).nullable(),
  environment: z.enum(['PRODUCTION', 'HOMOLOGATION']),
  competenceDate: z.iso.date(),
  operationNature: z.string(),
  customerRoleId: z.uuid().nullable(),
  supplierRoleId: z.uuid().nullable(),
  counterparty: FiscalDocumentCounterpartySchema.nullable(),
  series: z.string().nullable(),
  number: z.string().regex(/^\d+$/).nullable(),
  accessKey: z.string().nullable(),
  grossTotalCents: z.string(),
  discountTotalCents: z.string(),
  netTotalCents: z.string(),
  currency: z.string(),
  icmsTotalCents: z.string(),
  icmsStTotalCents: z.string(),
  fcpTotalCents: z.string(),
  difalTotalCents: z.string(),
  pisTotalCents: z.string(),
  cofinsTotalCents: z.string(),
  ipiTotalCents: z.string(),
  issTotalCents: z.string(),
  retentionTotalCents: z.string(),
  ibsTotalCents: z.string(),
  cbsTotalCents: z.string(),
  emissionInFlight: z.boolean(),
  lastProcessorErrorCode: z.string().nullable(),
  lastProcessorErrorMessage: z.string().nullable(),
  cancellationInFlight: z.boolean(),
  lastCancellationErrorCode: z.string().nullable(),
  lastCancellationErrorMessage: z.string().nullable(),
  cancelledAt: z.iso.datetime().nullable().optional(),
  artifactReferences: z.record(z.string(), z.string()).nullable(),
  items: z.array(FiscalDocumentItemDetailSchema),
  latestCalculationSnapshot: FiscalDocumentLatestSnapshotSchema.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export const CreateFiscalDocumentDraftItemSchema = z.looseObject({
  itemType: z.enum(['PRODUCT', 'SERVICE']),
  tenantProductId: z.uuid().optional(),
  catalogGenericProductId: z.uuid().optional(),
  ncmCode: z.string().max(10).optional(),
  tenantServiceId: z.uuid().optional(),
  description: z.string().min(1),
  quantityBp: z.string().regex(/^\d+$/),
  unitPriceCents: z.string().regex(/^\d+$/),
  discountCents: z.string().regex(/^\d+$/).optional(),
  serviceOrderItemId: z.uuid().optional(),
  originType: z
    .enum([
      'STOCK_PART',
      'LABOR_SERVICE',
      'THIRD_PARTY_SERVICE',
      'WARRANTY_PART',
      'INTERNAL_CONSUMPTION',
      'COURTESY',
      'BONUS',
      'RETURN_PART',
    ])
    .optional(),
});

export const CreateFiscalDocumentDraftBodySchema = z.looseObject({
  direction: z.enum(['INBOUND', 'OUTBOUND']),
  documentKind: z.enum(['NFE', 'NFCE', 'NFSE']).optional(),
  placeFiscalOperationId: z.uuid(),
  customerRoleId: z.uuid().optional(),
  supplierRoleId: z.uuid().optional(),
  competenceDate: z.iso.date(),
  environment: z.enum(['PRODUCTION', 'HOMOLOGATION']).optional(),
  items: z.array(CreateFiscalDocumentDraftItemSchema).min(1),
});

export const CreateFiscalDocumentDraftResponseSchema = z.looseObject({
  fiscalDocumentId: z.uuid(),
  status: z.string(),
  itemCount: z.number().int().nonnegative(),
});

export const CreateFiscalDocumentFromServiceOrderParamsSchema = z.looseObject({
  placeId: z.uuid(),
  serviceOrderId: z.uuid(),
});

export const CreateFiscalDocumentFromServiceOrderBodySchema = z.looseObject({
  nfePlaceFiscalOperationId: z.uuid().optional(),
  nfsePlaceFiscalOperationId: z.uuid().optional(),
  environment: z.enum(['PRODUCTION', 'HOMOLOGATION']).optional(),
});

export const CreatedFiscalDocumentFromServiceOrderSchema = z.looseObject({
  documentKind: z.enum(['NFE', 'NFSE']),
  fiscalDocumentId: z.uuid(),
  status: z.enum(FISCAL_DOCUMENT_STATUSES),
  alreadyExisted: z.boolean().optional(),
});

export const CreateFiscalDocumentFromServiceOrderResponseSchema = z.looseObject({
  documents: z.array(CreatedFiscalDocumentFromServiceOrderSchema).min(1).max(2),
  /** Backward-compatible alias for the NFE, or the first returned document. */
  fiscalDocumentId: z.uuid(),
  status: z.enum(FISCAL_DOCUMENT_STATUSES),
  itemCount: z.number().int().nonnegative(),
});

export const EmulateCaixaReceiveParamsSchema = CreateFiscalDocumentFromServiceOrderParamsSchema;

export const EmulateCaixaReceiveBodySchema = z.looseObject({
  discountCents: z
    .string()
    .regex(/^\d+$/, 'must be a non-negative integer string (cents)')
    .optional()
    .default('0'),
  lines: z.array(RegisterPaymentLineBodySchema).min(1),
});

export const EmulateCaixaReceiveDocumentSchema = z.looseObject({
  documentKind: z.enum(['NFE', 'NFSE']),
  fiscalDocumentId: z.uuid(),
  status: z.literal('AUTHORIZED'),
});

export const EmulateCaixaReceiveResponseSchema = z.looseObject({
  quitOnly: z.boolean(),
  paymentId: z.uuid(),
  documents: z.array(EmulateCaixaReceiveDocumentSchema),
});

export const CommercialAdjustItemSchema = z.looseObject({
  fiscalDocumentItemId: z.uuid(),
  quantityBp: z.string().regex(/^\d+$/).optional(),
  discountCents: z.string().regex(/^\d+$/).optional(),
  description: z.string().min(1).optional(),
});

export const CommercialAdjustBodySchema = z.looseObject({
  items: z.array(CommercialAdjustItemSchema).min(1),
});

export const FiscalDocumentMutationResponseSchema = z.looseObject({
  fiscalDocumentId: z.uuid(),
  status: z.string(),
  snapshotId: z.uuid(),
});

export const ValidateFiscalDocumentForIssueResponseSchema = z.looseObject({
  valid: z.literal(true),
});

export const IssueFiscalDocumentResponseSchema = z.looseObject({
  fiscalDocumentId: z.uuid(),
  status: z.enum(['AUTHORIZED', 'PENDING_AUTHORIZATION']),
  emissionInFlight: z.boolean(),
  accessKey: z.string().nullable(),
  number: z.string().regex(/^\d+$/).nullable(),
  series: z.string().nullable().optional(),
  protocolNumber: z.string().nullable(),
  environment: z.enum(['PRODUCTION', 'HOMOLOGATION']),
});

export const CancelFiscalDocumentBodySchema = z.object({
  justification: z.string().min(15).max(255),
});

export const CancelFiscalDocumentResponseSchema = z.looseObject({
  fiscalDocumentId: z.uuid(),
  status: z.literal('AUTHORIZED'),
  cancellationInFlight: z.literal(true),
});

export const GetFiscalDocumentArtifactParamsSchema = GetFiscalDocumentParamsSchema;

export const FiscalDocumentArtifactUrlResponseSchema = z.looseObject({
  downloadUrl: z.url(),
  expiresAt: z.iso.datetime(),
  contentType: z.string().min(1),
  disposition: z.enum(['inline', 'attachment']),
});

export type ListFiscalDocumentsQuery = z.infer<typeof ListFiscalDocumentsQuerySchema>;
export type GetFiscalDocumentParams = z.infer<typeof GetFiscalDocumentParamsSchema>;
export type CreateFiscalDocumentDraftBody = z.infer<typeof CreateFiscalDocumentDraftBodySchema>;
export type CreateFiscalDocumentFromServiceOrderBody = z.infer<
  typeof CreateFiscalDocumentFromServiceOrderBodySchema
>;
export type CreateFiscalDocumentFromServiceOrderResponse = z.infer<
  typeof CreateFiscalDocumentFromServiceOrderResponseSchema
>;
export type EmulateCaixaReceiveBody = z.infer<typeof EmulateCaixaReceiveBodySchema>;
export type EmulateCaixaReceiveResponse = z.infer<typeof EmulateCaixaReceiveResponseSchema>;
export type CommercialAdjustBody = z.infer<typeof CommercialAdjustBodySchema>;
export type ValidateFiscalDocumentForIssueResponse = z.infer<
  typeof ValidateFiscalDocumentForIssueResponseSchema
>;
export type IssueFiscalDocumentResponse = z.infer<typeof IssueFiscalDocumentResponseSchema>;
export type CancelFiscalDocumentBody = z.infer<typeof CancelFiscalDocumentBodySchema>;
export type CancelFiscalDocumentResponse = z.infer<typeof CancelFiscalDocumentResponseSchema>;
export type FiscalDocumentArtifactUrlResponse = z.infer<
  typeof FiscalDocumentArtifactUrlResponseSchema
>;
